import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { VagaObservacao } from "@/types/vaga-observacao";
import { MentionSelector } from "./MentionSelector";
import { Send } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toUserError } from "@/lib/api-error";

interface VagaObservacoesProps {
  vagaId: string;
}

interface User {
  id: string;
  full_name?: string;
  username?: string;
}

export function VagaObservacoes({ vagaId }: VagaObservacoesProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [novaMensagem, setNovaMensagem] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSearchTerm, setMentionSearchTerm] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: observacoes } = useQuery({
    queryKey: ["vaga-observacoes", vagaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vaga_observacoes")
        .select(`
          *,
          profiles (
            full_name,
            avatar_url
          )
        `)
        .eq("vaga_id", vagaId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as VagaObservacao[];
    },
  });

  const { data: usuarios } = useQuery({
    queryKey: ["usuarios"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, username");

      if (error) throw error;
      return data as User[];
    },
  });

  const adicionarObservacao = useMutation({
    mutationFn: async (mensagem: string) => {
      // 1. Inserir observacao
      const { data: observacao, error } = await supabase
        .from("vaga_observacoes")
        .insert([
          {
            vaga_id: vagaId,
            user_id: user?.id,
            mensagem,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      // 2. Identificar mencoes comparando com usuarios conhecidos
      // Para cada usuario, verificar se o texto contem @{nome_do_usuario}
      if (usuarios) {
        for (const u of usuarios) {
          const userName = u.full_name || u.username;
          if (!userName) continue;

          // Verificar se o texto contem @nome (case insensitive)
          const mentionPattern = `@${userName}`;
          if (mensagem.toLowerCase().includes(mentionPattern.toLowerCase())) {

            const { data: rpcResult, error: rpcError } = await supabase.rpc('create_mention_activity', {
              p_vaga_observacao_id: observacao.id,
              p_assigned_to: u.id,
              p_observacao: `Voce foi mencionado em uma observacao da vaga`,
            });

            if (rpcError) {
              console.error('[Mencoes] Erro ao criar atividade:', rpcError);
            } else {
              console.log('[Mencoes] Atividade criada com sucesso, id:', rpcResult);
            }
          }
        }
      }

      return observacao;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaga-observacoes", vagaId] });
      queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setNovaMensagem("");
      toast({
        title: "Sucesso",
        description: "Observação adicionada com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao adicionar observação:", error);
      toast({
        title: "Erro",
        description: "Não foi possível adicionar a observação. " + toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const position = e.target.selectionStart;

    setNovaMensagem(value);
    setCursorPosition(position);

    // Verificar se ha uma mencao sendo digitada (suporta caracteres Unicode/acentos)
    const textUpToCursor = value.slice(0, position);
    const mentionMatch = textUpToCursor.match(/@([\p{L}\p{M}]*)$/u);

    if (mentionMatch) {
      setMentionSearchTerm(mentionMatch[1]);
      setShowMentions(true);

      // Calcular posicao do dropdown
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        const rect = textarea.getBoundingClientRect();
        const lineHeight = 20; // Aproximado
        const lines = textUpToCursor.split('\n').length - 1;

        setMentionPosition({
          top: rect.top + (lines * lineHeight) + 25,
          left: rect.left + 10,
        });
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (selectedUser: User) => {
    const textUpToCursor = novaMensagem.slice(0, cursorPosition);
    const textAfterCursor = novaMensagem.slice(cursorPosition);
    
    // Encontrar onde começou a menção
    const mentionStartIndex = textUpToCursor.lastIndexOf('@');
    
    if (mentionStartIndex !== -1) {
      const beforeMention = novaMensagem.slice(0, mentionStartIndex);
      const userName = selectedUser.full_name || selectedUser.username || 'Usuário';
      const newText = `${beforeMention}@${userName} ${textAfterCursor}`;
      
      setNovaMensagem(newText);
      setShowMentions(false);
      
      // Focar no textarea e posicionar cursor
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + userName.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const handleEnviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaMensagem.trim()) return;
    adicionarObservacao.mutate(novaMensagem);
    setShowMentions(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      // Deixar o MentionSelector lidar com essas teclas
      return;
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar(e);
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      setShowMentions(false);
    };

    if (showMentions) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMentions]);

  const renderMensagemComMencoes = (mensagem: string) => {
    // Identificar mencoes baseado nos usuarios conhecidos
    if (!usuarios || usuarios.length === 0) {
      return mensagem;
    }

    // Criar lista de nomes de usuarios para buscar
    const userNames = usuarios
      .map(u => u.full_name || u.username)
      .filter((name): name is string => !!name)
      .sort((a, b) => b.length - a.length); // Ordenar por tamanho desc para evitar matches parciais

    // Criar regex que busca @nome para cada usuario
    const escapedNames = userNames.map(name =>
      name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const mentionRegex = new RegExp(`(@(?:${escapedNames.join('|')}))`, 'gi');

    const parts = mensagem.split(mentionRegex);

    return parts.map((part, index) => {
      // Verificar se esta parte e uma mencao (comeca com @)
      if (part.startsWith('@')) {
        const mentionName = part.slice(1);
        // Verificar se e um usuario valido
        const isValidUser = userNames.some(
          name => name.toLowerCase() === mentionName.toLowerCase()
        );
        if (isValidUser) {
          return (
            <span key={index} className="bg-blue-100 text-blue-700 px-1 rounded font-medium">
              {part}
            </span>
          );
        }
      }
      return part;
    });
  };

  return (
    <div className="flex flex-col h-[600px] bg-muted/30 rounded-lg relative">
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {observacoes?.map((obs) => (
            <div
              key={obs.id}
              className={`flex gap-3 ${
                obs.user_id === user?.id ? "flex-row-reverse" : ""
              }`}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {obs.profiles?.full_name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div
                className={`flex flex-col max-w-[80%] ${
                  obs.user_id === user?.id ? "items-end" : "items-start"
                }`}
              >
                <div
                  className={`rounded-lg p-3 ${
                    obs.user_id === user?.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm wrap-break-word whitespace-pre-wrap">
                    {renderMensagemComMencoes(obs.mensagem)}
                  </p>
                </div>
                <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                  <span>{obs.profiles?.full_name}</span>
                  <span>•</span>
                  <span>
                    {format(new Date(obs.created_at), "dd/MM/yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <form onSubmit={handleEnviar} className="p-4 border-t bg-background">
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={novaMensagem}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyPress}
            placeholder="Digite sua observação... (@ para mencionar usuários, Shift+Enter para quebrar linha, Enter para enviar)"
            className="resize-none"
            rows={3}
          />
          <Button type="submit" size="icon" disabled={!novaMensagem.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {showMentions && usuarios && (
        <MentionSelector
          users={usuarios}
          onSelect={handleMentionSelect}
          position={mentionPosition}
          searchTerm={mentionSearchTerm}
        />
      )}
    </div>
  );
}
